"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = buildMathTimeline;
require("regenerator-runtime/runtime");
const store2_1 = __importDefault(require("store2"));
// setup
const helpers_1 = require("../shared/helpers");
const taskSetup_1 = require("../taskSetup");
const sliderStimulus_1 = require("./trials/sliderStimulus");
const trials_1 = require("../shared/trials");
const config_1 = require("./helpers/config");
const taskStore_1 = require("../../taskStore");
function buildMathTimeline(config, mediaAssets) {
    const preloadTrials = (0, helpers_1.createPreloadTrials)(mediaAssets).default;
    (0, helpers_1.initTrialSaving)(config);
    const initialTimeline = (0, helpers_1.initTimeline)(config, trials_1.enterFullscreen);
    const ifRealTrialResponse = (trial) => {
        return {
            timeline: [(0, trials_1.getAudioResponse)(mediaAssets)],
            conditional_function: () => {
                const stim = trial || (0, taskStore_1.taskStore)().nextStimulus;
                if (stim.assessmentStage === 'practice_response' || stim.trialType === 'instructions') {
                    return false;
                }
                const trialsSkipped = (0, taskStore_1.taskStore)().trialsSkipped;
                if (trialsSkipped > 0) {
                    return false;
                }
                return true;
            },
        };
    };
    const timeline = [preloadTrials, initialTimeline];
    let corpus = (0, taskStore_1.taskStore)().corpora.stimulus;
    const downexCorpus = (0, taskStore_1.taskStore)().corpora.downex;
    const translations = (0, taskStore_1.taskStore)().translations;
    const validationErrorMap = {};
    const { runCat, heavyInstructions } = (0, taskStore_1.taskStore)();
    (0, taskStore_1.taskStore)('totalTrials', corpus.length);
    const layoutConfigMap = {};
    let i = 0;
    for (const c of [...downexCorpus, ...corpus]) {
        const { itemConfig, errorMessages } = (0, config_1.getLayoutConfig)(c, translations, mediaAssets, i);
        layoutConfigMap[c.itemId] = itemConfig;
        if (errorMessages.length) {
            validationErrorMap[c.itemId] = errorMessages.join('; ');
        }
        i += 1;
    }
    if (Object.keys(validationErrorMap).length) {
        console.error('The following errors were found');
        console.table(validationErrorMap);
        throw new Error('Something went wrong. Please look in the console for error details');
    }
    const terminateCat = runCat;
    const trialConfig = {
        trialType: 'audio',
        responseAllowed: true,
        promptAboveButtons: true,
        task: config.task,
        layoutConfigMap,
        terminateCat: terminateCat,
    };
    const feedbackBlock = (trial) => {
        return {
            timeline: [(0, trials_1.feedback)(true, 'feedbackCorrect', 'feedbackNotQuiteRight', false)],
            conditional_function: () => {
                return ((trial || (0, taskStore_1.taskStore)().nextStimulus).assessmentStage === 'practice_response' &&
                    (trial || (0, taskStore_1.taskStore)().nextStimulus).trialType === 'Number Line Slider');
            },
        };
    };
    const setupBlock = {
        timeline: [{ ...trials_1.setupStimulus, stimulus: '' }],
        conditional_function: () => {
            const trialsSkipped = (0, taskStore_1.taskStore)().trialsSkipped;
            if (trialsSkipped > 0) {
                (0, taskStore_1.taskStore)('trialsSkipped', trialsSkipped - 1);
                taskStore_1.taskStore.transact('testTrialCount', (oldVal) => oldVal + 1);
                return false;
            }
            else {
                return true;
            }
        },
    };
    const interBlockGap = {
        timeline: [{ ...trials_1.fixationOnly, stimulus: '', post_trial_gap: 350 }],
        conditional_function: () => {
            return (0, taskStore_1.taskStore)().trialsSkipped === 1;
        },
    };
    const afcStimulusBlock = (trial) => {
        return {
            timeline: [(0, trials_1.afcStimulusTemplate)(trialConfig, trial)],
            conditional_function: () => {
                var _a;
                const trialsSkipped = (0, taskStore_1.taskStore)().trialsSkipped;
                if (trialsSkipped > 0) {
                    return false;
                }
                return !((_a = (trial || (0, taskStore_1.taskStore)().nextStimulus).trialType) === null || _a === void 0 ? void 0 : _a.includes('Number Line'));
            },
        };
    };
    const sliderBlock = (trial) => {
        return {
            timeline: [(0, sliderStimulus_1.slider)(layoutConfigMap, terminateCat, trial), feedbackBlock(trial)],
            conditional_function: () => {
                var _a;
                const trialsSkipped = (0, taskStore_1.taskStore)().trialsSkipped;
                if (trialsSkipped > 0) {
                    return false;
                }
                return (_a = (trial || (0, taskStore_1.taskStore)().nextStimulus).trialType) === null || _a === void 0 ? void 0 : _a.includes('Number Line');
            },
        };
    };
    const sliderPractice = corpus.filter((trial) => {
        return trial.trialType === 'Number Line Slider' && trial.assessmentStage === 'practice_response';
    });
    // this block repeats all slider practice trials
    const repeatSliderPracticeBlock = () => {
        let trials = [];
        sliderPractice.forEach((trial, index) => {
            trials.push((0, sliderStimulus_1.slider)(layoutConfigMap, terminateCat, trial));
            if (index < sliderPractice.length - 1) {
                trials.push({
                    ...(0, trials_1.feedback)(true, 'feedbackCorrect', 'feedbackNotQuiteRight'),
                    conditional_function: () => {
                        return true;
                    },
                    post_trial_gap: 350,
                });
            }
        });
        return {
            timeline: [...trials],
            conditional_function: () => {
                return (!(0, taskStore_1.taskStore)().isCorrect &&
                    !(0, taskStore_1.taskStore)().testPhase &&
                    ((0, taskStore_1.taskStore)().nextStimulus.trialType === 'Number Line Slider' || runCat) &&
                    (0, taskStore_1.taskStore)().nextStimulus.assessmentStage === 'test_response');
            },
        };
    };
    const stimulusBlock = (trial) => {
        return {
            timeline: [afcStimulusBlock(trial), sliderBlock(trial), ifRealTrialResponse(trial)],
            conditional_function: () => {
                if ((0, taskStore_1.taskStore)().skipCurrentTrial) {
                    (0, taskStore_1.taskStore)('skipCurrentTrial', false);
                    return false;
                }
                const stim = trial || (0, taskStore_1.taskStore)().nextStimulus;
                const skipBlockTrialType = store2_1.default.page.get('skipCurrentBlock');
                if (stim.trialType === skipBlockTrialType && !runCat) {
                    return false;
                }
                else {
                    return true;
                }
            },
        };
    };
    if (runCat) {
        // puts the CAT portion of the corpus into taskStore and removes instructions
        const allCorpusParts = (0, helpers_1.prepareCorpus)(corpus, true, downexCorpus);
        const olderKidInstructionPractice = allCorpusParts.ipLight;
        const olderKidInstructions = olderKidInstructionPractice.filter((trial) => trial.trialType == 'instructions');
        let olderKidPractice = olderKidInstructionPractice.filter((trial) => trial.assessmentStage == 'practice_response');
        let olderKidBlocks = (0, helpers_1.prepareMultiBlockCat)((0, taskStore_1.taskStore)().corpora.stimulus);
        (0, taskStore_1.taskStore)('corpora', { stimulus: olderKidBlocks, downex: (0, taskStore_1.taskStore)().corpora.downex });
        (0, taskStore_1.taskStore)('totalTestTrials', 0); // add to this while building out each block
        // don't repeat instructions
        const usedIds = [];
        // first add downex trials to the timeline
        if (heavyInstructions) {
            const downexInstructionPractice = allCorpusParts.ipHeavy;
            const downexInstructions = downexInstructionPractice.filter((trial) => trial.trialType == 'instructions');
            let downexPractice = downexInstructionPractice.filter((trial) => trial.assessmentStage == 'practice_response');
            let downexBlock = allCorpusParts.downexCat;
            // remove items from first block that are already in subsequent blocks
            const nonDownexIds = [];
            olderKidBlocks.flat().map((trial) => nonDownexIds.push(trial.itemId));
            downexBlock = downexBlock.filter((trial) => {
                return !nonDownexIds.includes(trial.itemId);
            });
            // filter practice trials to only include appropriate trial types if downward extension
            const excludedDownexPracticeTypes = [
                'Addition',
                'Number Comparison',
                'Number Identification',
                'Counting',
                'Counting AFC',
            ];
            downexPractice = downexPractice.filter((trial) => !excludedDownexPracticeTypes.includes(trial.trialType));
            const allowedIds = ['math-instructions1-heavy', 'math-intro1-heavy'];
            downexInstructions.forEach((trial) => {
                if (allowedIds.includes(trial.itemId)) {
                    timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
                    timeline.push((0, trials_1.afcStimulusTemplate)(trialConfig, trial));
                }
            });
            downexPractice.forEach((trial) => {
                timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
                timeline.push(stimulusBlock(trial));
            });
            timeline.push((0, trials_1.practiceTransition)());
            const numOfTrials = Math.floor(downexBlock.length / 2);
            taskStore_1.taskStore.transact('totalTestTrials', (oldVal) => (oldVal += numOfTrials));
            for (let j = 0; j < numOfTrials; j++) {
                timeline.push({ ...trials_1.setupDownex, stimulus: '' }); // select only from the current block
                timeline.push(stimulusBlock());
            }
        }
        const numOfBlocks = olderKidBlocks.length;
        const trialProportionsPerBlock = [4, 6, 6]; // divide by these numbers to get trials per block
        for (let i = 0; i < numOfBlocks; i++) {
            // push in block-specific instructions
            const blockInstructions = olderKidInstructions.filter((trial) => {
                let allowedIDs; // CAT only uses particular instructions from corpus
                switch (i) {
                    case 0:
                        allowedIDs = heavyInstructions ? ['math-intro2'] : ['math-instructions1', 'math-intro1'];
                        break;
                    case 1:
                        allowedIDs = ['math-intro2'];
                        break;
                    case 2:
                        allowedIDs = ['math-intro2', 'number-line-instruct1'];
                        break;
                    default:
                        allowedIDs = [];
                }
                const include = allowedIDs.includes(trial.itemId) && !usedIds.includes(trial.itemId);
                if (include) {
                    usedIds.push(trial.itemId);
                }
                return include;
            });
            blockInstructions.forEach((trial) => {
                timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
                timeline.push((0, trials_1.afcStimulusTemplate)(trialConfig, trial));
            });
            // push in block-specific practice trials
            const blockPractice = olderKidPractice.filter((trial) => {
                return i === Number(trial.block_index);
            });
            blockPractice.forEach((trial) => {
                timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
                timeline.push(stimulusBlock(trial));
                if (trial.trialType === 'Number Line Slider') {
                    timeline.push(feedbackBlock());
                }
            });
            // final slider block
            if (i === 2) {
                timeline.push(repeatSliderPracticeBlock());
            }
            // practice transition screen
            timeline.push((0, trials_1.practiceTransition)());
            // push in random items at start of first block (after practice trials)
            if (i === 0) {
                allCorpusParts.start.forEach((trial) => timeline.push(stimulusBlock(trial)));
            }
            const numOfTrials = Math.floor(olderKidBlocks[i].length / trialProportionsPerBlock[i]);
            taskStore_1.taskStore.transact('totalTestTrials', (oldVal) => (oldVal += numOfTrials));
            for (let j = 0; j < numOfTrials; j++) {
                timeline.push({ ...(0, trials_1.setupStimulusFromBlock)(i), stimulus: '' }); // select only from the current block
                timeline.push(stimulusBlock());
            }
            allCorpusParts.unnormed.forEach((trial) => {
                if (i === Number(trial.block_index)) {
                    timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
                    timeline.push(stimulusBlock(trial));
                }
            });
        }
    }
    else {
        (0, taskStore_1.taskStore)('totalTestTrials', (0, helpers_1.getRealTrials)(corpus));
        // if cat is not running, remove difficulty field from all items
        corpus.forEach((trial) => (trial.difficulty = NaN));
        const newCorpora = {
            downex: (0, taskStore_1.taskStore)().corpora.downex,
            stimulus: heavyInstructions ? downexCorpus : corpus,
        };
        (0, taskStore_1.taskStore)('corpora', newCorpora);
        const numOfTrials = heavyInstructions ? (0, taskStore_1.taskStore)().totalDownexTrials : (0, taskStore_1.taskStore)().totalTrials;
        for (let i = 0; i < numOfTrials; i++) {
            timeline.push(setupBlock);
            timeline.push(repeatSliderPracticeBlock());
            timeline.push((0, trials_1.practiceTransition)());
            timeline.push(interBlockGap);
            timeline.push(stimulusBlock());
        }
    }
    (0, taskSetup_1.initializeCat)();
    timeline.push((0, trials_1.taskFinished)());
    timeline.push(trials_1.exitFullscreen);
    return { jsPsych: taskSetup_1.jsPsych, timeline };
}
